import React, { useCallback, useEffect, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import {
  SignalBar,
  ClinicalDataGrid,
  StatusPill,
  CodeChip,
  fmtMoney,
  tokens,
} from "../../shared/clinical-design";
import { MISSING_SEVERITY } from "../common/constants";
import * as svc from "../services/chargeCaptureService";

/**
 * Missing Charges Dashboard (N.3): encounters completados con nota pero sin
 * charge — revenue que se está perdiendo, priorizado por horas transcurridas.
 * Fuente futura: GET /api/charges/missing.
 */
export default function MissingChargesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await svc.getMissingCharges());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleResolve = async (row) => {
    // Acción local-only hasta que exista POST /api/charges/missing/{id}/resolve
    await svc.resolveMissingCharge(row.id);
    load();
  };

  const s = data?.summary;
  const pending = (data?.items || []).filter((i) => !i.resolved);

  return (
    <Stack spacing={1.5}>
      {s && (
        <SignalBar
          signals={[
            { label: "Completed appts", value: s.appointmentsCompleted },
            { label: "Charges processed", value: s.chargesProcessed, tone: "ok" },
            { label: "Missing", value: s.missingCount, tone: s.missingCount > 0 ? "crit" : "ok" },
            {
              label: "Missing rate",
              value: `${s.missingRate}%`,
              trend: `target ${s.monthlyTargetPct}%`,
              tone: s.missingRate > s.monthlyTargetPct ? "warn" : "ok",
            },
            { label: "Revenue at risk", value: fmtMoney(s.revenueAtRisk), tone: "crit", hint: "Estimated unbilled revenue from encounters without charges" },
            { label: "Monthly $ at risk", value: fmtMoney(s.monthlyRevenueAtRisk), tone: "warn" },
          ]}
        />
      )}
      <ClinicalDataGrid
        loading={loading}
        rows={pending}
        rowKey={(r) => r.id}
        emptyMessage="No encounters with missing charges. Documentation → billing loop is closed."
        columns={[
          {
            key: "severity",
            header: "Severity",
            width: 150,
            render: (r) => {
              const sev = MISSING_SEVERITY[r.severity] || MISSING_SEVERITY.NORMAL;
              return <StatusPill tone={sev.tone} label={sev.label} dense />;
            },
          },
          { key: "patientName", header: "Patient" },
          { key: "cipId", header: "CIP ID", width: 130, render: (r) => <CodeChip code={r.cipId} /> },
          { key: "providerName", header: "Provider", width: 140 },
          { key: "dateOfService", header: "DOS", width: 110, mono: true },
          {
            key: "hoursElapsed",
            header: "Hours since visit",
            align: "right",
            width: 130,
            mono: true,
            render: (r) => (
              <Typography
                component="span"
                sx={{
                  fontFamily: tokens.fontMono,
                  fontSize: 13,
                  color: r.hoursElapsed > 72 ? tokens.crit : r.hoursElapsed > 48 ? tokens.warn : tokens.ink,
                }}
              >
                {r.hoursElapsed}h
              </Typography>
            ),
          },
          {
            key: "actions",
            header: "",
            align: "right",
            width: 140,
            render: (r) => (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResolve(r);
                }}
                sx={{ textTransform: "none", fontSize: 12, py: 0, borderColor: tokens.primary, color: tokens.primary }}
              >
                Create charge
              </Button>
            ),
          },
        ]}
      />
    </Stack>
  );
}
